import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EnergyDashboard } from '@/components/projector-watch/EnergyDashboard';
import { SleepAnalysis } from '@/components/projector-watch/SleepAnalysis';
import { NotificationCenter } from '@/components/projector-watch/NotificationCenter';
import { EnergyGraph } from '@/components/projector-watch/EnergyGraph';
import { EnergyHistory } from '@/components/projector-watch/EnergyHistory';
import { Settings } from '@/components/projector-watch/Settings';
import { BackButton } from '@/components/ui/back-button';
import { motion } from 'framer-motion';
import { LayoutDashboard, History, Settings as SettingsIcon, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';

export default function ProjectorWatchUserDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [energyHistory, setEnergyHistory] = useState([]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        if (!currentUser) {
          navigate('/login');
          return;
        }

        // Check beta access
        const { data: betaAccess, error: betaError } = await supabase
          .from('beta_access')
          .select('status')
          .eq('user_id', currentUser.id)
          .eq('program', 'projector-watch')
          .single();

        if (betaError) throw betaError;

        const hasAccess = betaAccess?.status === 'approved' || betaAccess?.status === 'testing';
        if (!hasAccess) {
          toast({
            variant: "destructive",
            title: "Accès refusé",
            description: "Vous n'avez pas encore accès à cette fonctionnalité.",
          });
          navigate('/projector-watch');
          return;
        }

        // Load energy history
        const { data: historyData, error: historyError } = await supabase
          .from('energy_history')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('timestamp', { ascending: false });

        if (historyError) throw historyError;
        setEnergyHistory(historyData || []);

      } catch (error) {
        console.error('Error checking access:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de vérifier vos droits d'accès.",
        });
        navigate('/projector-watch');
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [currentUser, navigate, toast]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen pt-20 px-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto space-y-6">
        <BackButton to="/projector-watch" />

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="grid grid-cols-3 gap-4">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4" />
              Tableau de bord
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Historique
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <EnergyDashboard />
                <EnergyGraph history={energyHistory} />
              </div>
              <div className="space-y-6">
                <SleepAnalysis />
                <NotificationCenter />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <EnergyHistory history={energyHistory} />
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Settings />
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
}